/**
 * Estonian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const et: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Töölaud',
  'nav.funds': 'Fondid',
  'nav.investments': 'Investeeringud',
  'nav.investees': 'Investeeringuobjektid',
  'nav.esg': 'ESG Ülevaade',
  'nav.compliance': 'Vastavus',
  'nav.reports': 'Aruanded',
  'nav.settings': 'Seaded',
  'nav.users': 'Kasutajad',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Näitajad',
  'nav.taxonomy': 'EL-i taksonoomia',
  'nav.filings': 'Esitised',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Esita andmed',
  'nav.submissions': 'Minu esitised',
  'nav.benchmarks': 'Võrdlusalused',
  'nav.messages': 'Sõnumid',
  'nav.portfolio': 'Portfell',
  'nav.performance': 'Fonditulemused',
  'nav.esgSummary': 'ESG Kokkuvõte',
  'nav.pipeline': 'Konveier',
  'nav.dueDiligence': 'Hoolsuskohustus',
  'nav.impact': 'Mõjunäitajad',
  'nav.sdg': 'SAE kaardistamine',
  'nav.audit': 'Auditijälg',
  'nav.snapshots': 'Andmete hetktõmmised',
  'nav.knowledgeGraph': 'Teadmusgraaf',
  'nav.geographic': 'Geograafiline',
  'nav.bi': 'BI Stuudio',
  'nav.ai': 'AI Assistent',
  'nav.tasks': 'Ülesanded',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatiivne monitor',

  // Common Actions
  'action.create': 'Loo',
  'action.edit': 'Muuda',
  'action.delete': 'Kustuta',
  'action.save': 'Salvesta',
  'action.cancel': 'Tühista',
  'action.submit': 'Esita',
  'action.approve': 'Kinnita',
  'action.reject': 'Lükka tagasi',
  'action.download': 'Laadi alla',
  'action.export': 'Ekspordi',
  'action.import': 'Impordi',
  'action.filter': 'Filtreeri',
  'action.search': 'Otsi',
  'action.refresh': 'Värskenda',
  'action.close': 'Sulge',
  'action.back': 'Tagasi',
  'action.next': 'Järgmine',
  'action.previous': 'Eelmine',
  'action.viewAll': 'Vaata kõiki',
  'action.viewDetails': 'Vaata detaile',
  'action.generate': 'Genereeri',
  'action.signOut': 'Logi välja',
  'action.addNew': 'Lisa uus',
  'action.invite': 'Kutsu',

  // Statuses
  'status.draft': 'Mustand',
  'status.submitted': 'Esitatud',
  'status.validated': 'Valideeritud',
  'status.approved': 'Kinnitatud',
  'status.rejected': 'Tagasi lükatud',
  'status.pending': 'Ootel',
  'status.inProgress': 'Pooleli',
  'status.overdue': 'Hilinenud',
  'status.compliant': 'Vastav',
  'status.filed': 'Esitatud',
  'status.active': 'Aktiivne',
  'status.inactive': 'Mitteaktiivne',
  'status.closed': 'Suletud',
  'status.fundraising': 'Vahendite kogumine',
  'status.investing': 'Investeerimine',
  'status.harvesting': 'Koristamine',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfelli ülevaade',
  'dashboard.complianceStatus': 'Vastavuse staatus',
  'dashboard.collectionProgress': 'Kogumise edenemine',
  'dashboard.activityFeed': 'Tegevus',
  'dashboard.quickActions': 'Kiirtoimingud',
  'dashboard.investmentPipeline': 'Investeeringukonveier',
  'dashboard.totalFunds': 'Fonde kokku',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktiivsed investeeringud',
  'dashboard.totalInvestees': 'Investeeringuobjektid',
  'dashboard.recentActivity': 'Hiljutine tegevus',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Kokku kohustusi',
  'lp.capitalCalled': 'Nõutud kapital',
  'lp.distributed': 'Jaotatud',
  'lp.netPosition': 'Netopositsioon',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fonditulemused',
  'lp.esgSummary': 'ESG Kokkuvõte',
  'lp.reportLibrary': 'Aruannete kogu',
  'lp.taxonomyAligned': 'Taksonoomiaga kooskõlas',
  'lp.paiCoverage': 'PAI Katvus',
  'lp.portfolioPerformance': 'LP Portfelli tulemused',

  // Fund Labels
  'fund.name': 'Fondi nimi',
  'fund.code': 'Fondi kood',
  'fund.vintage': 'Asutamisaasta',
  'fund.size': 'Fondi suurus',
  'fund.targetSize': 'Sihtsuurus',
  'fund.currency': 'Valuuta',
  'fund.strategy': 'Strateegia',
  'fund.status': 'Staatus',
  'fund.sfdrCategory': 'SFDR Kategooria',
  'fund.geographyFocus': 'Geograafiline fookus',
  'fund.sectorFocus': 'Sektori fookus',

  // Form Labels
  'form.name': 'Nimi',
  'form.email': 'E-post',
  'form.country': 'Riik',
  'form.amount': 'Summa',
  'form.currency': 'Valuuta',
  'form.date': 'Kuupäev',
  'form.description': 'Kirjeldus',
  'form.notes': 'Märkmed',
  'form.type': 'Tüüp',
  'form.period': 'Periood',
  'form.year': 'Aasta',
  'form.required': 'Kohustuslik',
  'form.optional': 'Valikuline',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Kohustus',
  'table.called': 'Nõutud',
  'table.distributed': 'Jaotatud',
  'table.tvpi': 'TVPI',
  'table.status': 'Staatus',
  'table.regulation': 'Regulatsioon',
  'table.filingType': 'Esitise tüüp',
  'table.reportingPeriod': 'Periood',
  'table.createdAt': 'Loodud',
  'table.actions': 'Toimingud',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EL-i taksonoomia',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Andmete täielikkus',
  'compliance.nextDeadline': 'Järgmine tähtaeg',

  // Reports
  'report.quarterly': 'Kvartali aruanne',
  'report.annual': 'Aastaaruanne',
  'report.paiStatement': 'PAI Avaldus',
  'report.taxonomyReport': 'Taksonoomia aruanne',
  'report.generatedAt': 'Genereeritud',
  'report.version': 'Versioon',

  // General
  'general.loading': 'Laadimine...',
  'general.noData': 'Andmed puuduvad',
  'general.error': 'Ilmnes viga',
  'general.retry': 'Proovi uuesti',
  'general.total': 'Kokku',
  'general.page': 'Leht',
  'general.of': '/',
  'general.showing': 'Kuvatakse',
  'general.results': 'tulemust',
  'general.language': 'Keel',
  'general.english': 'Inglise',
  'general.french': 'Prantsuse',
};
